import {buildSemanticTokens} from '../buildSemanticTokens';
import type {DesignSystemDefinition} from '../types';

const neutral = {
  'white-50': 'rgba(255, 255, 255, 0.5)',
  'primary-black': 'black',
  900: '#0c0407',
  800: '#333',
  700: '#5a6570',
  600: '#666',
  500: '#666',
  400: '#999',
  300: '#a8b0b8',
  200: '#dfe3e6',
  100: '#e5e5e5',
  'black-50': '#f7f8f9',
  transparent: 'rgba(255, 255, 255, 0)',
};

/** Desaturated slate-teal scale for professional HR product UI */
const accentMain = '#556872';

const greenColors = {
  2: '#152428',
  3: '#e8ecee',
  4: '#d4dadc',
  5: '#f4f6f7',
  30: accentMain,
  40: '#8fa5ab',
  50: '#e8ecee',
  70: '#6b848c',
  80: '#253f45',
  100: '#4a6670',
  200: '#3a565e',
  500: '#2f4f56',
  600: '#3a565e',
  700: '#1e2a31',
  800: '#404040',
  850: '#757575',
  900: '#152428',
};

/** Orange secondary scale — muted warm accent for page chrome (CTAs stay brand teal) */
const orangeColors = {
  3: '#dfe3e6',
  4: '#f0e9df',
  5: '#f6ebd8',
  6: '#ffffff',
  7: '#fafafa',
  40: '#e8cba8',
  70: '#d4a574',
  100: '#d4844a',
  500: '#d4844a',
  800: '#d9d9d9',
  900: '#c86a38',
  950: '#944f2a',
};

const primary = {
  30: '#D9D9D9',
  50: '#a7a7a7',
  70: '#757575',
  100: '#565656',
  200: '#404040',
};

const gradientApp =
  'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, #f6ebd8f0 55%, #ffffff 100%),' +
  ' radial-gradient(circle farthest-corner at 100% 0%, rgba(200, 106, 56, 0.06) 0%, transparent 58%),' +
  ' radial-gradient(circle farthest-corner at 0% 0%, rgba(232, 203, 168, 0.25) 0%, transparent 62%)';

const gradientNavbar =
  'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 251, 252, 0.94) 100%),' +
  ' radial-gradient(circle farthest-corner at 0% 0%, rgba(232, 236, 238, 0.85) 0%, transparent 62%),' +
  ' radial-gradient(circle farthest-corner at 100% 0%, rgba(47, 79, 86, 0.07) 0%, transparent 58%)';

const scrollbar = {
  track: greenColors[4],
  thumb: greenColors[70],
  thumbHover: orangeColors[40],
};

const semantic = buildSemanticTokens(
  {neutral, greenColors, orangeColors},
  {
    scorePill: {
      high: {bgcolor: '#f0f4f2', color: '#2d5c48', strokeColor: '#6b9180'},
      medium: {bgcolor: '#f5f2ed', color: '#5c4e3a', strokeColor: '#8a7a62'},
      low: {bgcolor: '#f6f0ef', color: '#8b3a35', strokeColor: '#b05a54'},
    },
    alignmentStatus: {
      metAccent: '#2d5c48',
      gapAccent: '#8b3a35',
      gapLabel: '#6f2e2a',
      metChipBg: '#f0f4f2',
      metChipFg: '#2d5c48',
      gapChipBg: '#f6f0ef',
      gapChipFg: '#8b3a35',
      warningChipBg: '#f5f2ed',
      warningChipFg: '#5c4e3a',
    },
    info: {bg: '#eef2f5', fg: '#3d5568'},
  }
);

export const designSystem20260616: DesignSystemDefinition = {
  version: '20260616',
  neutral,
  greenColors,
  orangeColors,
  primary,
  warningColor: '#8b3a35',
  warningColorLight: '#d4a8a4',
  warningColorDark: '#6f2e2a',
  actions: {
    cta: greenColors[500],
    ctaHover: greenColors[80],
    ctaDisabled: '#b8c4c7',
    primary: '#2A8C96',
    primaryHover: '#237883',
    destructive: '#8b3a35',
    destructiveHover: '#6f2e2a',
  },
  accent: {
    main: accentMain,
    subtle: '#e8ecee',
    icon: accentMain,
  },
  text: {
    primary: '#1e2a31',
    secondary: '#5a6570',
  },
  empionOrangeGradient: {
    base: gradientApp,
    medium: gradientApp,
    dark: gradientApp,
    navbar: gradientNavbar,
  },
  scrollbar,
  lightModePalette: {
    primary: {
      main: greenColors[500],
      contrastText: '#ffffff',
    },
    secondary: {
      main: orangeColors[900],
      light: orangeColors[500],
      dark: '#a85a30',
    },
    background: {
      default: orangeColors[5],
      paper: '#fff',
      menu: '#fff',
      dark: greenColors[3],
    },
    text: {
      primary: greenColors[700],
      secondary: neutral[700],
    },
    info: {
      main: '#3d5568',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#5c4e3a',
      contrastText: '#ffffff',
    },
  },
  ...semantic,
};
